import { useState, useEffect, useMemo } from 'react';
import Modal from '../Modal';
import { ClockIcon } from '@heroicons/react/24/outline';
import { useGraphQL } from '../../graphql';

const CRON_PRESETS = [
  { label: 'Every 30 minutes', cron: '*/30 * * * *' },
  { label: 'Every hour', cron: '0 * * * *' },
  { label: 'Every 6 hours', cron: '0 */6 * * *' },
  { label: 'Nightly at 1am', cron: '0 1 * * *' },
  { label: 'Nightly at 3am', cron: '0 3 * * *' },
  { label: 'Daily at noon', cron: '0 12 * * *' },
  { label: 'Weekly Monday 2am', cron: '0 2 * * 1' },
];

const ScheduleWorkflowModal = ({ isOpen, onClose, template, onScheduled }) => {
  const { executeQuery, executeMutation, queries, mutations } = useGraphQL();
  const [cronSchedule, setCronSchedule] = useState('0 1 * * *');
  const [runMode, setRunMode] = useState('per_company'); // 'single' | 'per_company'
  const [staggerSeconds, setStaggerSeconds] = useState(10);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [workflowInput, setWorkflowInput] = useState('{}');
  const [inputError, setInputError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load companies when per_company mode is selected
  useEffect(() => {
    if (!isOpen || runMode !== 'per_company' || companies.length > 0) return;

    const loadCompanies = async () => {
      setCompaniesLoading(true);
      try {
        const data = await executeQuery(queries.getCompanies(), { limit: 200, offset: 0 });
        setCompanies(data.adminCompanies || []);
      } catch (err) {
        console.error('Failed to load companies:', err);
      } finally {
        setCompaniesLoading(false);
      }
    };
    loadCompanies();
  }, [isOpen, runMode]);

  // Build default workflow input from template inputSchema
  const defaultInput = useMemo(() => {
    if (!template?.inputSchema) return {};
    const defaults = {};
    template.inputSchema.forEach(param => {
      if (param.default !== null && param.default !== undefined) {
        defaults[param.name] = param.default;
      }
    });
    return defaults;
  }, [template]);

  // Reset form when modal opens or template changes
  useEffect(() => {
    if (!isOpen) return;
    setCronSchedule('0 1 * * *');
    setRunMode('per_company');
    setStaggerSeconds(10);
    setSelectedCompanyIds([]);
    setInputError(null);
    setWorkflowInput(JSON.stringify(defaultInput, null, 2));
  }, [isOpen, template?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInputError(null);

    let parsedInput = null;
    try {
      const raw = JSON.parse(workflowInput);
      if (Object.keys(raw).length > 0) parsedInput = raw;
    } catch {
      setInputError('Invalid JSON');
      return;
    }

    setSubmitting(true);
    try {
      const variables = {
        templateName: template.name,
        input: parsedInput,
        cronSchedule,
      };

      let result;
      if (runMode === 'per_company') {
        variables.staggerSeconds = staggerSeconds;
        if (selectedCompanyIds.length > 0) {
          variables.companyIds = selectedCompanyIds;
        }
        result = await executeMutation(mutations.adminRunCompanyWorkflows, variables);
        const response = result?.adminRunCompanyWorkflows;
        if (response?.success) {
          onScheduled?.();
          onClose();
        } else {
          setInputError(response?.error || 'Failed to schedule');
        }
      } else {
        result = await executeMutation(mutations.adminRunWorkflow, variables);
        const response = result?.adminRunWorkflow;
        if (response?.success) {
          onScheduled?.();
          onClose();
        } else {
          setInputError(response?.error || 'Failed to schedule');
        }
      }
    } catch (err) {
      setInputError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCompany = (companyId) => {
    setSelectedCompanyIds(prev =>
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  if (!template) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`SCHEDULE: ${template.name.toUpperCase()}`} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-400 font-mono">
          {template.description}
        </p>

        {/* Cron Schedule */}
        <div>
          <label className="block text-xs font-mono text-gray-400 uppercase mb-1">
            Cron Schedule <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={cronSchedule}
            onChange={(e) => setCronSchedule(e.target.value)}
            required
            placeholder="0 1 * * *"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white font-mono text-sm focus:border-white focus:outline-none"
          />
          <div className="flex flex-wrap gap-1 mt-2">
            {CRON_PRESETS.map(preset => (
              <button
                key={preset.cron}
                type="button"
                onClick={() => setCronSchedule(preset.cron)}
                className={`px-2 py-0.5 font-mono text-xs border transition-colors ${
 cronSchedule === preset.cron
 ? 'bg-primary text-on-accent border-white'
 : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-500'
 }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-600 mt-1">Format: minute hour day month weekday</p>
        </div>

        {/* Run Mode */}
        <div>
          <label className="block text-xs font-mono text-gray-400 uppercase mb-1">Run Mode</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setRunMode('single')}
              className={`flex-1 px-3 py-2 font-mono text-xs font-bold border transition-colors ${
 runMode === 'single'
 ? 'bg-primary text-on-accent border-white'
 : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-500'
 }`}
            >
              SINGLE WORKFLOW
            </button>
            <button
              type="button"
              onClick={() => setRunMode('per_company')}
              className={`flex-1 px-3 py-2 font-mono text-xs font-bold border transition-colors ${
 runMode === 'per_company'
 ? 'bg-primary text-on-accent border-white'
 : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-500'
 }`}
            >
              PER COMPANY
            </button>
          </div>
        </div>

        {/* Per-company options */}
        {runMode === 'per_company' && (
          <>
            <div>
              <label className="block text-xs font-mono text-gray-400 uppercase mb-1">
                Stagger Seconds
              </label>
              <input
                type="number"
                value={staggerSeconds}
                onChange={(e) => setStaggerSeconds(parseInt(e.target.value, 10) || 0)}
                min={0}
                className="w-32 px-3 py-2 bg-gray-800 border border-gray-700 text-white font-mono text-sm focus:border-white focus:outline-none"
              />
              <p className="text-xs text-gray-600 mt-1">Delay between starting each company workflow</p>
            </div>

            <div>
              <label className="block text-xs font-mono text-gray-400 uppercase mb-1">
                Company Filter <span className="text-gray-600">(optional - empty = all eligible)</span>
              </label>
              {companiesLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                  <div className="animate-spin h-3 w-3 border border-gray-400 border-t-transparent rounded-full" />
                  Loading companies...
                </div>
              ) : (
                <div className="max-h-40 overflow-y-auto bg-gray-800 border border-gray-700">
                  {companies.map(company => (
                    <label
                      key={company.id}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCompanyIds.includes(company.id)}
                        onChange={() => toggleCompany(company.id)}
                        className="accent-white"
                      />
                      <span className="font-mono text-xs text-white">{company.name}</span>
                      <span className="font-mono text-xs text-gray-500">{company.size}</span>
                    </label>
                  ))}
                </div>
              )}
              {selectedCompanyIds.length > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  {selectedCompanyIds.length} selected
                  <button
                    type="button"
                    onClick={() => setSelectedCompanyIds([])}
                    className="ml-2 text-gray-500 hover:text-white"
                  >
                    clear
                  </button>
                </p>
              )}
            </div>
          </>
        )}

        {/* Workflow Input */}
        <div>
          <label className="block text-xs font-mono text-gray-400 uppercase mb-1">
            Workflow Input (JSON)
          </label>
          <textarea
            value={workflowInput}
            onChange={(e) => {
              setWorkflowInput(e.target.value);
              setInputError(null);
            }}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white font-mono text-sm focus:border-white focus:outline-none h-32"
          />
          {template.inputSchema?.length > 0 && (
            <p className="text-xs text-gray-600 mt-1">
              Params: {template.inputSchema.map(p => p.name).join(', ')}
            </p>
          )}
        </div>

        {/* Error */}
        {inputError && (
          <div className="bg-red-50 border border-red-200 p-2 font-mono text-xs text-red-700">
            {inputError}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-gray-400 font-mono text-xs font-bold hover:bg-gray-700 uppercase"
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={submitting || !cronSchedule.trim()}
            className="px-4 py-2 bg-violet-600 text-white font-mono text-xs font-bold hover:bg-violet-500 disabled:opacity-50 flex items-center gap-2 uppercase"
          >
            <ClockIcon className="w-4 h-4" />
            {submitting ? 'SCHEDULING...' : 'CREATE SCHEDULE'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ScheduleWorkflowModal;
